// src/ui/components/EventModal.documents.js
// Pestana "Documentos" del modal de evento: checklist de confirmacion, subida
// del contrato firmado y del justificante de la senal, e historial documental.

import {
    listEventDocuments,
    getConfirmationStatus,
    uploadSignedContract,
    uploadReceivedDocument,
    getDocumentSignedUrl,
    deleteEventDocument,
    DOC_KINDS,
    DOC_KIND_LABELS,
} from "../../services/eventDocumentsService.js";
import { updateEvent } from "../../services/eventsService.js";
import { listPaymentsByEvent, createPayment } from "../../services/paymentsService.js";
import { splitHonorarios } from "../../services/pdf/generateContrato.js";
import { getCurrentTeamId, getCurrentUserAndProfile } from "../../services/teamService.js";
import { actions } from "../../app/state.js";
import { escapeHtml } from "./EventModal.utils.js";

const fmtEur = (n) => new Intl.NumberFormat("es-ES", {
    style: "currency", currency: "EUR", useGrouping: "always",
}).format(Number(n) || 0);

const STATUS_BADGE = {
    emitido:  { label: "Emitido",  cls: "bg-amber-100 text-amber-700" },
    firmado:  { label: "Firmado",  cls: "bg-emerald-100 text-emerald-700" },
    recibido: { label: "Recibido", cls: "bg-emerald-100 text-emerald-700" },
    anulado:  { label: "Anulado",  cls: "bg-slate-200 text-slate-500" },
    pendiente:{ label: "Pendiente",cls: "bg-slate-100 text-slate-500" },
};

/**
 * Cablea la pestana de documentos.
 * @param {HTMLElement} root  - raiz del modal
 * @param {object} ev         - evento actual
 * @param {function} onStageChange - callback tras confirmar automaticamente
 * @param {function} generateReceipt - abre la emision del recibo de la senal
 */
export async function wireDocumentsTab(root, ev, { onStageChange = null, generateReceipt = null } = {}) {
    if (!ev?.id) return;

    const $ = (sel) => root.querySelector(sel);
    const listEl = $("#event-docs-list");
    const itemsEl = $("#event-docs-checklist-items");
    const badgeEl = $("#event-docs-checklist-badge");
    const targetEl = $("#event-docs-contract-target");
    if (!listEl) return;

    // Equipo y usuario se resuelven en el momento de subir, no al abrir: si se
    // pidieran aqui y la llamada fallara, los botones se quedarian sin cablear
    // y el usuario haria clic sin que ocurriera nada.
    let _ctx = null;
    async function getCtx() {
        if (_ctx) return _ctx;
        const teamId = await getCurrentTeamId({ profileOptions: { force: false, allowNull: true } });
        let userId = null;
        try {
            const { user } = await getCurrentUserAndProfile({ profileOptions: { force: false } });
            userId = user?.id || null;
        } catch { /* la autoria es opcional */ }
        _ctx = { teamId, userId };
        return _ctx;
    }

    let documents = [];
    let advanceTotal = 0;

    // La senal pactada es el porcentaje de reserva con IVA que fija el
    // contrato, no una cifra fija: se deriva del importe y del reparto
    // acordado en el evento para precargar el campo.
    const expectedSenal = Number(ev.amount) > 0
        ? splitHonorarios(Number(ev.amount), ev.deposit_pct).reservaTotal
        : 0;

    // ── Render ────────────────────────────────────────────────────────────────

    async function refreshAdvances() {
        const payments = await listPaymentsByEvent(ev.id);
        advanceTotal = (payments || [])
            .filter((p) => p.payment_type === "advance")
            .reduce((acc, p) => acc + (Number(p.amount) || 0), 0);

        const hint = $("#event-docs-receipt-hint");
        const input = $("#event-docs-receipt-amount");
        if (!hint || !input) return;

        const parts = [];
        if (expectedSenal > 0) parts.push(`Señal pactada: ${fmtEur(expectedSenal)}`);
        parts.push(advanceTotal > 0 ? `registrado: ${fmtEur(advanceTotal)}` : "sin ingresos registrados");
        hint.textContent = parts.join(" · ");
        hint.className = advanceTotal > 0
            ? "text-[11px] text-emerald-600 mb-3"
            : "text-[11px] text-slate-400 mb-3";

        // Solo se precarga mientras no haya nada cobrado, para no sugerir un
        // segundo ingreso por el mismo importe.
        if (!input.value && advanceTotal <= 0 && expectedSenal > 0) {
            input.value = expectedSenal.toFixed(2);
        }
    }

    function renderChecklist() {
        const { ok, items } = getConfirmationStatus(documents);

        itemsEl.innerHTML = items.map((it) => `
            <div class="flex items-center gap-2.5 text-sm">
              <i class="fa-solid ${it.done ? "fa-circle-check text-emerald-500" : "fa-circle text-slate-300"} text-xs"></i>
              <span class="${it.done ? "text-slate-700" : "text-slate-400"}">${escapeHtml(it.label)}</span>
              ${it.done ? `<span class="text-[10px] text-slate-400 ml-auto">${escapeHtml(it.doc?.file_name || "")}</span>` : ""}
            </div>
        `).join("");

        const alreadyConfirmed = ["confirmed", "done"].includes(ev.stage);
        if (alreadyConfirmed) {
            badgeEl.textContent = "Confirmado";
            badgeEl.className = "shrink-0 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700";
        } else if (ok) {
            badgeEl.textContent = "Completo";
            badgeEl.className = "shrink-0 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700";
        } else {
            const done = items.filter((i) => i.done).length;
            badgeEl.textContent = `${done} de ${items.length}`;
            badgeEl.className = "shrink-0 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700";
        }
    }

    function renderContractTarget() {
        // Un contrato emitido sin fichero es el hueco natural donde encaja el
        // firmado: asi conserva su numero correlativo.
        const pending = documents.find(
            (d) => d.kind === DOC_KINDS.CONTRATO && d.origin === "emitido" && !d.file_path && d.status !== "anulado"
        );
        targetEl.innerHTML = pending
            ? `Se adjuntará a <strong class="font-mono text-slate-600">${escapeHtml(pending.doc_number)}</strong>`
            : `<span class="text-amber-600">Sin contrato emitido pendiente: se registrará como suelto.</span>`;
        return pending;
    }

    function renderList() {
        if (!documents.length) {
            listEl.innerHTML = `<div class="text-xs text-slate-400 italic py-4 text-center">
                Todavía no hay documentos para este evento.</div>`;
            return;
        }

        listEl.innerHTML = documents.map((d) => {
            const badge = STATUS_BADGE[d.status] || STATUS_BADGE.pendiente;
            const label = DOC_KIND_LABELS[d.kind] || d.kind;
            const when = d.uploaded_at || d.issued_at || d.created_at;
            const date = when ? new Date(when).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" }) : "";

            return `
            <div class="flex items-center gap-3 p-3 rounded-xl border border-slate-200 bg-white">
              <i class="fa-solid ${d.origin === "emitido" ? "fa-file-contract text-amber-600" : "fa-receipt text-emerald-600"} text-sm w-4"></i>
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-2 flex-wrap">
                  <span class="text-sm font-medium text-slate-800">${escapeHtml(label)}</span>
                  ${d.doc_number ? `<span class="text-[11px] font-mono text-slate-500">${escapeHtml(d.doc_number)}</span>` : ""}
                  <span class="px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${badge.cls}">${badge.label}</span>
                </div>
                <div class="text-[11px] text-slate-400 truncate">
                  ${escapeHtml(d.file_name || "Sin archivo adjunto")}${date ? " · " + date : ""}
                </div>
              </div>
              ${d.file_path ? `
                <button type="button" class="btn-doc-view shrink-0 px-2.5 py-1.5 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors"
                  data-path="${escapeHtml(d.file_path)}" title="Ver">
                  <i class="fa-solid fa-eye text-xs"></i>
                </button>
                <button type="button" class="btn-doc-del shrink-0 px-2.5 py-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                  data-id="${escapeHtml(d.id)}" title="Eliminar archivo">
                  <i class="fa-solid fa-trash text-xs"></i>
                </button>` : ""}
            </div>`;
        }).join("");

        listEl.querySelectorAll(".btn-doc-view").forEach((btn) => {
            btn.onclick = async () => {
                const url = await getDocumentSignedUrl(btn.dataset.path);
                if (url) window.open(url, "_blank", "noopener");
                else actions.addToast?.({ type: "error", message: "No se pudo abrir el documento" });
            };
        });

        listEl.querySelectorAll(".btn-doc-del").forEach((btn) => {
            btn.onclick = async () => {
                const doc = documents.find((d) => d.id === btn.dataset.id);
                if (!doc) return;
                const isIssued = doc.origin === "emitido";
                const msg = isIssued
                    ? "¿Eliminar el archivo firmado? El número de contrato se conserva como anulado."
                    : "¿Eliminar este documento?";
                if (!window.confirm(msg)) return;

                try {
                    await deleteEventDocument(doc);
                    actions.addToast?.({ type: "success", message: "Documento eliminado" });
                    await refresh();
                } catch (e) {
                    console.error("[docs] delete:", e);
                    actions.addToast?.({ type: "error", message: "Error al eliminar" });
                }
            };
        });
    }

    async function refresh() {
        documents = await listEventDocuments(ev.id);
        await refreshAdvances();
        renderChecklist();
        renderContractTarget();
        renderList();
    }

    // ── Confirmacion automatica ───────────────────────────────────────────────

    async function maybeConfirm() {
        // Solo propone avanzar desde "senal pendiente": nunca retrocede una
        // fase ni reabre un evento ya confirmado o realizado.
        if (ev.stage !== "accepted_pending_deposit") return;
        if (!getConfirmationStatus(documents).ok) return;

        // Se pregunta, no se impone: la fase la decide siempre el usuario.
        const yes = window.confirm(
            "Ya están subidos el contrato firmado y el justificante de la señal.\n\n"
            + "¿Pasar el evento a Confirmado?"
        );
        if (!yes) return;

        try {
            await updateEvent(ev.id, {
                stage: "confirmed",
                confirmed_at: new Date().toISOString(),
                deposit_received_at: new Date().toISOString(),
                stage_updated_at: new Date().toISOString(),
            });
            ev.stage = "confirmed";
            actions.addToast?.({
                type: "success",
                message: "Contrato y justificante recibidos: el evento pasa a Confirmado",
            });
            renderChecklist();
            onStageChange?.("confirmed");
        } catch (e) {
            console.error("[docs] no se pudo confirmar el evento:", e);
            actions.addToast?.({
                type: "warning",
                message: "Documentos guardados, pero no se pudo cambiar la fase. Hazlo manualmente.",
            });
        }
    }

    // Tras anotar la senal se ofrece su recibo, que toma el importe de los
    // pagos registrados.
    async function offerReceipt() {
        if (!generateReceipt) return;
        if (!window.confirm("Señal registrada. ¿Descargar el recibo de la señal?")) return;
        await generateReceipt();
    }

    // ── Subidas ───────────────────────────────────────────────────────────────

    function wireUpload(btnSel, inputSel, dropSel, handler) {
        const btn = $(btnSel);
        const input = $(inputSel);
        const dropZone = $(dropSel);

        if (!btn || !input) {
            console.error("[docs] no se encontraron los controles de subida:", btnSel, inputSel);
            return;
        }

        async function runUpload(file) {
            if (!file) return;

            const original = btn.innerHTML;
            btn.disabled = true;
            btn.innerHTML = '<i class="fa-solid fa-circle-notch fa-spin text-[10px]"></i> Subiendo...';

            try {
                // El handler puede devolver un paso posterior, que va al final
                // para no solapar su dialogo con el de confirmar el evento.
                const followUp = await handler(file);
                actions.addToast?.({ type: "success", message: "Documento subido" });
                await refresh();
                await maybeConfirm();
                await followUp?.();
            } catch (e) {
                console.error("[docs] upload:", e);
                actions.addToast?.({ type: "error", message: e.message || "Error al subir el archivo" });
            } finally {
                btn.disabled = false;
                btn.innerHTML = original;
                input.value = ""; // permite volver a elegir el mismo fichero
            }
        }

        btn.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            input.click();
        });

        input.addEventListener("change", () => runUpload(input.files?.[0]));

        // Arrastrar y soltar sobre la tarjeta
        if (!dropZone) return;

        const setDragging = (on) => {
            dropZone.classList.toggle("border-slate-900", on);
            dropZone.classList.toggle("bg-slate-50", on);
            dropZone.classList.toggle("border-slate-200", !on);
        };

        // dragover debe cancelarse o el navegador abre el fichero en una pestaña.
        ["dragenter", "dragover"].forEach((type) =>
            dropZone.addEventListener(type, (e) => {
                e.preventDefault();
                e.stopPropagation();
                setDragging(true);
            })
        );

        ["dragleave", "dragend"].forEach((type) =>
            dropZone.addEventListener(type, (e) => {
                e.preventDefault();
                // Los hijos emiten dragleave al moverse dentro: solo cuenta salir de la tarjeta.
                if (e.relatedTarget && dropZone.contains(e.relatedTarget)) return;
                setDragging(false);
            })
        );

        dropZone.addEventListener("drop", (e) => {
            e.preventDefault();
            e.stopPropagation();
            setDragging(false);
            runUpload(e.dataTransfer?.files?.[0]);
        });
    }

    wireUpload("#event-docs-contract-btn", "#event-docs-contract-input", "#event-docs-contract-card", async (file) => {
        const { teamId, userId } = await getCtx();
        const pending = renderContractTarget();
        await uploadSignedContract({
            teamId, eventId: ev.id, file, userId,
            documentId: pending?.id || null,
            docNumber: pending?.doc_number || null,
        });
    });

    wireUpload("#event-docs-receipt-btn", "#event-docs-receipt-input", "#event-docs-receipt-card", async (file) => {
        const { teamId, userId } = await getCtx();
        const amountEl = $("#event-docs-receipt-amount");
        const amount = Number(amountEl?.value) || 0;

        await uploadReceivedDocument({
            teamId, eventId: ev.id, kind: DOC_KINDS.JUSTIFICANTE_SENAL, file, userId,
            notes: amount > 0 ? `Transferencia de ${fmtEur(amount)}` : null,
        });

        // El justificante es la prueba del cobro: es el momento natural de
        // anotar el ingreso, sea cual sea el importe acordado.
        if (amount > 0) {
            try {
                await createPayment({
                    team_id: teamId,
                    event_id: ev.id,
                    amount,
                    payment_type: "advance",
                    payment_date: new Date().toISOString().slice(0, 10),
                    method: "transfer",
                    note: "Señal — justificante adjuntado",
                    created_by: userId,
                });
                actions.addToast?.({ type: "success", message: `Señal de ${fmtEur(amount)} registrada` });
                if (amountEl) amountEl.value = "";
                return offerReceipt;
            } catch (e) {
                console.error("[docs] no se pudo registrar el ingreso:", e);
                actions.addToast?.({
                    type: "warning",
                    message: "Justificante subido, pero el ingreso no se registró. Añádelo en Finanzas.",
                });
            }
        }
    });

    // Los controles ya estan cableados: la carga de datos va despues y aislada,
    // para que un fallo de red deje la pestaña utilizable y con un aviso
    // visible en lugar de en blanco.
    try {
        await refresh();
    } catch (e) {
        console.error("[docs] no se pudieron cargar los documentos:", e);
        listEl.innerHTML = `<div class="text-xs text-rose-600 py-4 text-center">
            No se pudieron cargar los documentos. Puedes subir archivos igualmente.</div>`;
    }
}
